import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, UserCircle2, Save, Link2 } from "lucide-react";
import { useAuth, startGoogleLogin } from "@/context/AuthContext";
import { getMyProfile, saveMyProfile, searchProfiles, claimProfile } from "@/lib/api";

export default function Profile() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [nickname, setNickname] = useState("");
  const [handicap, setHandicap] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const p = await getMyProfile();
        setProfile(p);
        setName(p?.name || user.name || "");
        setNickname(p?.nickname || "");
        setHandicap(p?.handicap ?? "");
        if (!p && user.name) {
          const found = await searchProfiles(user.name.split(" ")[0]);
          setMatches(found.filter((x) => !x.linked).slice(0, 5));
        }
      } catch (e) { console.error("profile load failed", e); }
      finally { setLoading(false); }
    })();
  }, [user, authLoading]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const p = await saveMyProfile({ name: name.trim(), nickname: nickname.trim(), handicap: parseFloat(handicap) || 0 });
      setProfile(p); setMatches([]);
      toast.success("Profile saved");
    } catch { toast.error("Could not save profile"); } finally { setSaving(false); }
  };

  const claim = async (id) => {
    try {
      const p = await claimProfile(id);
      setProfile(p); setName(p.name); setNickname(p.nickname); setHandicap(p.handicap); setMatches([]);
      toast.success(`Linked to "${p.nickname || p.name}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not link profile"); }
  };

  return (
    <div className="max-w-xl mx-auto px-5 pt-8 pb-16 tga-fade">
      <button data-testid="profile-back-btn" onClick={() => navigate("/")} className="ghost-btn rounded-lg px-3 py-2 text-xs mb-4 inline-flex items-center gap-1">
        <ChevronLeft size={14} /> Home
      </button>
      <div className="text-center mb-6">
        <div className="crest crest-lg mx-auto mb-4"><UserCircle2 size={34} /></div>
        <h1 className="font-display text-4xl font-semibold text-[color:var(--brass-bright)]">My profile</h1>
        <p className="mt-2 text-[color:var(--muted-green)] italic font-display">Your nickname and handicap, ready for any organiser to pick you into a trip.</p>
      </div>

      <div className="tga-card" data-testid="profile-card">
        {authLoading || loading ? (
          <p className="helptext text-[color:var(--text-dark)]/70">Loading…</p>
        ) : !user ? (
          <div className="text-center py-4">
            <p className="helptext text-[color:var(--text-dark)]/80 mb-3">Sign in to create your player profile.</p>
            <button data-testid="profile-signin-btn" onClick={startGoogleLogin} className="brass-btn rounded-lg px-4 py-2 text-sm">Sign in with Google</button>
          </div>
        ) : (
          <div className="space-y-4">
            {!profile && matches.length > 0 && (
              <div className="border border-dashed border-[color:var(--brass-dark)] rounded-lg p-3 bg-[rgba(197,160,89,0.06)]" data-testid="profile-claim-box">
                <div className="text-xs uppercase text-[color:var(--brass-dark)] mb-1 inline-flex items-center gap-1"><Link2 size={12} /> Is one of these you?</div>
                <p className="helptext text-[color:var(--text-dark)]/70 mb-2">An organiser may have already added you to the directory. Link it to keep your history.</p>
                <div className="flex flex-wrap gap-2">
                  {matches.map((m) => (
                    <button key={m.id} data-testid={`profile-claim-${m.id}`} onClick={() => claim(m.id)} className="ghost-btn rounded-full px-3 py-1 text-xs">
                      {m.nickname ? `${m.nickname} (${m.name})` : m.name} · HCP {m.handicap}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs uppercase tracking-wide text-[color:var(--brass-dark)] mb-1">Full name</label>
              <input data-testid="profile-name-input" className="tga-input-light w-full" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-[color:var(--brass-dark)] mb-1">Nickname</label>
              <input data-testid="profile-nickname-input" className="tga-input-light w-full" placeholder="What the lads call you" value={nickname} onChange={(e) => setNickname(e.target.value)} />
              <p className="helptext text-[color:var(--text-dark)]/60 mt-1">Shown on scorecards and leaderboards when set.</p>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-[color:var(--brass-dark)] mb-1">Handicap</label>
              <input data-testid="profile-handicap-input" type="number" step="0.1" className="tga-input-light w-32" value={handicap} onChange={(e) => setHandicap(e.target.value)} />
            </div>
            <button data-testid="profile-save-btn" disabled={saving} className="brass-btn rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1" onClick={save}>
              <Save size={14} /> {saving ? "Saving…" : "Save profile"}
            </button>
            {profile && <p className="helptext text-[color:var(--text-dark)]/60">Signed in as {user.email}. Organisers can find you by name or nickname when setting up a trip.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
